import React from 'react';
import type { HourCum } from '../homeApi';
import { minutesOf } from '../format';
import { f1, linePath, niceMax, textWidth } from './scale';
import { useWidth } from './useWidth';

/**
 * «Σήμερα ως τώρα»: αθροιστικές εισπράξεις ανά ώρα — σήμερα (100% + απαλό εμβαδόν) ως το «τώρα», χθες
 * (ουδέτερο γκρι) όλη μέρα. Οι ώρες έρχονται με τη σειρά της περιόδου (ένα μαγαζί που κλείνει 02:00
 * έχει τις 00–01 στο τέλος)· κάθε θέση i καλύπτει [i, i+1) και η αθροιστική της τιμή μπαίνει στο τέλος της.
 */
export interface CumulativeModel {
  rows: HourCum[];
  /** Πεδίο x σε θέσεις (δείκτες του rows). */
  x0: number;
  x1: number;
  today: Array<[number, number]>;
  yesterday: Array<[number, number]>;
  nowX: number | null;
  /** Τελευταία θέση με τιμή σήμερα. */
  lastToday: number;
  todayEnd: number;
  yesterdayEnd: number;
}

export function buildCumulative(hourly: HourCum[], until: string): CumulativeModel | null {
  const rows = hourly;
  const n = rows.length;
  if (n === 0) return null;
  const s = rows.findIndex(r => (r.today ?? 0) > 0 || r.yesterday > 0);
  if (s < 0) return null;
  let lastToday = -1;
  rows.forEach((r, i) => { if (r.today !== null && r.today !== undefined) lastToday = i; });
  let lastYesterday = s;
  for (let i = s + 1; i < n; i += 1) if (rows[i].yesterday > rows[i - 1].yesterday) lastYesterday = i;

  const nowMin = minutesOf(until);
  let nowX: number | null = null;
  if (nowMin !== null) {
    const j = rows.findIndex(r => r.hour === Math.floor(nowMin / 60));
    if (j >= 0) nowX = j + (nowMin % 60) / 60;
  }
  if (nowX === null && lastToday >= 0) nowX = lastToday + 1;
  if (nowX !== null && nowX < s) nowX = s;

  const x0 = s;
  let x1 = Math.max(lastYesterday + 1, nowX ?? 0, lastToday + 1);
  if (x1 - x0 < 3) x1 = Math.min(n, x0 + 3);
  if (x1 <= x0) x1 = x0 + 1;

  const today: Array<[number, number]> = [[s, 0]];
  for (let i = s; i <= lastToday; i += 1) {
    const v = rows[i].today;
    if (v === null || v === undefined) continue;
    today.push([nowX !== null ? Math.min(i + 1, nowX) : i + 1, v]);
  }
  const todayEnd = today[today.length - 1][1];
  if (nowX !== null && today[today.length - 1][0] < nowX) today.push([nowX, todayEnd]);

  const yesterday: Array<[number, number]> = [[s, 0]];
  for (let i = s; i <= lastYesterday; i += 1) yesterday.push([i + 1, rows[i].yesterday]);
  const yesterdayEnd = Math.max(...rows.map(r => r.yesterday));

  return { rows, x0, x1, today, yesterday, nowX, lastToday, todayEnd, yesterdayEnd };
}

export interface HourLineProps {
  model: CumulativeModel;
  height: number;
  ariaLabel: string;
  /** «2.000 €» για το πάνω άκρο, «1.000» για τα ενδιάμεσα. */
  axisLabel: (v: number, isTop: boolean) => string;
  hourLabel: (hour: number) => string;
  endLabel: string;
  nowLabel: string;
  tip: (row: HourCum, next: HourCum | undefined) => string;
}

export const HourLine: React.FC<HourLineProps> = ({ model, height, ariaLabel, axisLabel, hourLabel, endLabel, nowLabel, tip }) => {
  const [ref, width] = useWidth();
  const left = 44;
  const top = 18;
  const bottom = 24;
  const right = Math.max(56, textWidth(endLabel, 12, 600) + 16);
  const pw = Math.max(40, width - left - right);
  const ph = Math.max(20, height - top - bottom);
  const base = top + ph;
  const { x0, x1 } = model;
  const X = (x: number) => left + (pw * (x - x0)) / (x1 - x0);
  const max = niceMax(Math.max(model.todayEnd, model.yesterdayEnd) * 1.05);
  const Y = (v: number) => top + ph * (1 - Math.max(0, v) / max);
  const toPts = (pts: Array<[number, number]>): Array<[number, number]> => pts.map(([x, v]) => [X(x), Y(v)]);
  const todayPts = toPts(model.today);
  const end = todayPts[todayPts.length - 1];
  const area = `${linePath(todayPts)}L${f1(end[0])} ${base}L${f1(X(x0))} ${base}Z`;
  const nowXpx = model.nowX !== null ? f1(X(model.nowX)) + 0.5 : null;
  const nowW = textWidth(nowLabel, 11, 400);
  const nowAnchor = nowXpx !== null && nowXpx + nowW / 2 > width ? 'end' : 'middle';
  const slots: number[] = [];
  for (let i = Math.ceil(x0); i < x1 && i < model.rows.length; i += 1) slots.push(i);

  return (
    <div ref={ref} className="xh-chart" data-testid="hour-line">
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={ariaLabel}>
        {[0, max / 2, max].map(v => (
          <text key={v} className="xh-axl s" x={left - 8} y={f1(Y(v) + 4)} textAnchor="end">{axisLabel(v, v === max)}</text>
        ))}
        <path className="xh-area" d={area} />
        {model.yesterday.length > 1 && <path className="xh-ln n" d={linePath(toPts(model.yesterday))} data-testid="line-yesterday" />}
        <path className="xh-ln v1" d={linePath(todayPts)} data-testid="line-today" />
        {nowXpx !== null && (
          <g>
            <line className="xh-nowl" x1={nowXpx} x2={nowXpx} y1={top - 6} y2={base} />
            <text className="xh-axl s" x={nowXpx} y={top - 9} textAnchor={nowAnchor}>{nowLabel}</text>
          </g>
        )}
        <circle className="xh-dot" cx={f1(end[0])} cy={f1(end[1])} r={4.5} />
        <text className="xh-vl b" x={f1(end[0] + 9)} y={f1(end[1] + 4)}>{endLabel}</text>
        {slots.filter(i => model.rows[i].hour % 3 === 0).map(i => (
          <text key={`x${i}`} className="xh-axl" x={f1(X(i))} y={base + 17} textAnchor="middle">{hourLabel(model.rows[i].hour)}</text>
        ))}
        <line className="xh-ax" x1={left} x2={left + pw} y1={base + 0.5} y2={base + 0.5} />
        {slots.filter(i => i <= model.lastToday).map(i => (
          <rect key={`h${i}`} className="xh-hit" x={f1(X(i))} y={top} width={f1(X(i + 1) - X(i))} height={ph}>
            <title>{tip(model.rows[i], model.rows[i + 1])}</title>
          </rect>
        ))}
      </svg>
    </div>
  );
};

export default HourLine;
